import React from 'react';
import { observable, reaction } from 'mobx';
import { GCP_ENTITY_TYPES } from '@kentik/ui-shared/hybrid/constants';
import { memoize, orderBy, get } from 'lodash';
import { inject, observer } from 'mobx-react';
import styled from 'styled-components';
import {
  Card,
  Button,
  Flex,
  Popover,
  Icon,
  Tag,
  Text,
  Spinner,
  Box,
  Heading,
  Checkbox,
  Suspense,
  Tooltip
} from 'core/components';
import { Filter, VirtualizedTable } from 'core/components/table';
import GoogleLogo from 'app/components/GoogleLogo';
import CloudIcon from 'app/views/hybrid/maps/components/CloudIcon';
import TimedCallout from 'app/views/hybrid/maps/components/TimedCallout';
import { Field, Select, RemoveButton, Form } from 'core/form';
import ProjectsFilterCollection from 'app/stores/clouds/gcp/ProjectsFilterCollection';
import MapSearchGroupDialog from './components/MapSearchGroupDialog';

const GCP_PROJECT_GROUPS_MODULE_GROUP = 'gcpprojectgroups';

const GCP_PROJECT_SUMMARY_ENTITIES = [
  GCP_ENTITY_TYPES.NETWORK,
  GCP_ENTITY_TYPES.SUBNET,
  GCP_ENTITY_TYPES.ROUTER,
  GCP_ENTITY_TYPES.VPN_GATEWAY,
  GCP_ENTITY_TYPES.INTERCONNECT
];

const StyledBox = styled(Box)`
  .flex-grow-1 {
    flex-grow: 1;
  }
`;

const initialFields = {
  subscriptions: {},
  name: {
    label: 'Name / ID'
  },
  view: {
    label: 'View',
    options: [
      { value: 'all', label: 'All' },
      { value: 'selected', label: 'Selected' }
    ],
    defaultValue: 'all'
  },
  group: {
    label: 'Group',
    options: [{ value: '', label: '---' }],
    defaultValue: ''
  }
};

@Form({ fields: initialFields, options: { name: 'gcp-projects-filter' } })
@inject('$clouds', '$hybridMap', '$auth', '$moduleConfig')
@observer
export default class MapSearchGcpProjectsPanel extends React.Component {
  gcpCloudMapCollectionDisposer = () => {};

  @observable
  projectsCollection = new ProjectsFilterCollection([]);

  tempSelectedGroup = '';

  state = {
    popoverOpen: false,
    calloutMessageArr: [],
    displayGroupDialog: false,
    isManagingProjectGroup: false
  };

  initializeProjectsCollectionFromTopology() {
    const { $hybridMap, form } = this.props;
    const { gcpCloudMapCollection } = $hybridMap;

    const projectModels = gcpCloudMapCollection.gcpProjectEntities;

    const projectsSummary = gcpCloudMapCollection.Summary?.[GCP_ENTITY_TYPES.PROJECT] ?? {};

    const defaultSummary = GCP_PROJECT_SUMMARY_ENTITIES.reduce((carry, entityType) => {
      carry[entityType] = 0;
      return carry;
    }, {});

    projectModels.forEach((projectModel) => {
      const projectId = projectModel.projectId ?? projectModel.id;
      projectModel.projectId ??= projectId;
      projectModel.summary = projectsSummary[projectId] ?? { ...defaultSummary };

      // default to id if not set
      projectModel.name ??= projectModel.name ?? projectModel.Name ?? projectId;
    });

    const sortedModels = orderBy(
      projectModels,
      (model) => +get(model, `summary.${GCP_ENTITY_TYPES.NETWORK}`, 0),
      'desc'
    );
    this.projectsCollection.set(sortedModels);

    // autoselect group and filter to show only projects that apply to this group
    if ($hybridMap.selectedGcpProjectGroup && this.companyGcpProjectGroups[$hybridMap.selectedGcpProjectGroup]) {
      form.setValue('group', $hybridMap.selectedGcpProjectGroup);
      this.onGroupDropdownChange({ value: $hybridMap.selectedGcpProjectGroup });

      return;
    }

    // autoselect previous projects based on user settings.
    const selectedGcpProjectsInUserOptions = $hybridMap.selectedGcpProjects;
    if (selectedGcpProjectsInUserOptions.length > 0) {
      this.projectsCollection.unfiltered.forEach((gcpProjectModel) => {
        if (selectedGcpProjectsInUserOptions.includes(gcpProjectModel.get('projectId'))) {
          gcpProjectModel.select({ multi: true });
        }
      });
    }

    this.projectsCollection.applyPagination();
  }

  async fetchModuleConfig() {
    const { $moduleConfig } = this.props;

    return $moduleConfig.fetchModuleConfig(GCP_PROJECT_GROUPS_MODULE_GROUP);
  }

  componentDidMount() {
    const { $hybridMap } = this.props;

    const promiseFunction = async () => {
      await this.fetchModuleConfig();

      this.initializeProjectsCollectionFromTopology();
    };

    promiseFunction();

    this.gcpCloudMapCollectionDisposer = reaction(
      () => $hybridMap.gcpCloudMapCollection.gcpProjectEntities.length,
      () => this.initializeProjectsCollectionFromTopology()
    );
  }

  componentWillUnmount() {
    // execute to avoid memory leak
    this.gcpCloudMapCollectionDisposer();
  }

  get isLoading() {
    const { $hybridMap } = this.props;
    return $hybridMap.gcpCloudMapCollection.loading;
  }

  onPopoverClose = () => {
    const { onChange, $auth, form } = this.props;

    // do nothing when topology is loading
    if (this.isLoading) {
      return null;
    }

    this.setState({ popoverOpen: false }, () => {
      // do not persist in demo account
      onChange({
        persist: !$auth.isDemoUser,
        gcpProjectIds: this.projectsCollection.selectedProjectIds,
        selectedGroup: form.getValue('group')
      });
    });

    return null;
  };

  onGroupDropdownChange = ({ value }) => {
    this.tempSelectedGroup = value;

    if (value !== '' && this.companyGcpProjectGroups[value]) {
      this.projectsCollection.addGroupFilter(this.companyGcpProjectGroups[value]);
    }

    if (value === '') {
      this.projectsCollection.removeGroupFilter();
      this.projectsCollection.applyPagination();
    }
  };

  changePage = (page) => {
    this.projectsCollection.setPage(page);
    this.projectsCollection.applyPageChangeFilter();
  };

  onViewDropdownChange = ({ value }) => {
    if (value === 'selected') {
      this.projectsCollection.showSelectedProjects();
      return;
    }

    if (value === 'all') {
      this.projectsCollection.showAllProjects();
      return;
    }

    console.warn('Unhandled inViewDropdownChange', value);
  };

  get columns() {
    const columns = [
      {
        key: 'projectId',
        label: 'Selected',
        width: 50,
        renderer: ({ model }) => (
          <Checkbox mt="2px" checked={model.isSelected} onChange={() => model.select({ multi: true })} />
        )
      },
      {
        key: 'name',
        label: 'Name',
        name: 'name',
        className: 'flex-grow-1',
        flexGrow: 1,
        width: 'auto',
        renderer: ({ model }) => (
          <Flex flexDirection="column" gap={1}>
            <Box>
              <Text as="b">{model.get('name')}</Text>
            </Box>
            {model.get('name') !== model.get('projectId') && (
              <Box>
                <Text muted small>
                  {model.get('projectId')}
                </Text>
              </Box>
            )}
          </Flex>
        )
      },
      {
        key: 'summary',
        label: 'Summary',
        name: 'summary',
        resizable: false,
        renderer: ({ model }) => {
          const summary = model.get('summary');

          if (!summary) {
            return null;
          }

          return (
            <Flex gap={1} flexWrap="wrap" justifyContent="space-evenly">
              {GCP_PROJECT_SUMMARY_ENTITIES.map((entityType) => (
                <Tooltip key={entityType} content={entityType}>
                  <Flex gap={1} flexDirection="column" alignItems="center">
                    <CloudIcon cloudProvider="gcp" entity={entityType} iconSize={20} />
                    <Text>{summary[entityType] ?? 0}</Text>
                  </Flex>
                </Tooltip>
              ))}
            </Flex>
          );
        }
      }
    ];

    return columns;
  }

  get companyGcpProjectGroups() {
    const { $moduleConfig } = this.props;
    return $moduleConfig.get(GCP_PROJECT_GROUPS_MODULE_GROUP, '', {});
  }

  cancelManageGroup = () => {
    this.projectsCollection.addGroupFilter(this.companyGcpProjectGroups[this.tempSelectedGroup]);
    this.tempSelectedGroup = null;

    this.setState({ isManagingProjectGroup: false });
  };

  saveGroup = async () => {
    const { $moduleConfig } = this.props;

    return $moduleConfig
      .saveModuleConfig({
        module: GCP_PROJECT_GROUPS_MODULE_GROUP,
        settings: {
          ...this.companyGcpProjectGroups,
          [this.tempSelectedGroup]: this.projectsCollection.selectedProjectIds
        }
      })
      .finally(() => {
        this.setState({
          displayGroupDialog: false,
          isManagingProjectGroup: false,
          calloutMessageArr: [`${this.tempSelectedGroup} group has been saved.`]
        });

        this.tempSelectedGroup = null;
      });
  };

  createGroup = async (groupName) => {
    const { $moduleConfig } = this.props;

    const message = this.tempSelectedGroup
      ? `${this.tempSelectedGroup} group has been renamed into ${groupName}.`
      : `Group ${groupName} was successfully created.`;

    return $moduleConfig
      .saveModuleConfig({
        module: GCP_PROJECT_GROUPS_MODULE_GROUP,
        settings: {
          ...this.companyGcpProjectGroups,
          [groupName]: this.tempSelectedGroup
            ? this.companyGcpProjectGroups[this.tempSelectedGroup]
            : this.projectsCollection.selectedProjectIds
        }
      })
      .finally(() => {
        this.setState({
          displayGroupDialog: false,
          calloutMessageArr: [message]
        });

        // if group was renamed
        if (this.tempSelectedGroup) {
          this.removeGroup(this.tempSelectedGroup, true);
          this.onGroupDropdownChange({ value: groupName });
          this.tempSelectedGroup = null;
        }
      });
  };

  removeGroup = async (groupNameToRemove = '', ignoreCalloutMessage = false) => {
    const { $moduleConfig, form } = this.props;

    const field = form.getField('group');
    const groupName = groupNameToRemove || field.getValue('group');

    field.setValue('');

    const companyProjectGroups = { ...this.companyGcpProjectGroups };
    delete companyProjectGroups[groupName];

    this.onGroupDropdownChange({ value: '' });

    const newState = {
      displayGroupDialog: false
    };
    if (!ignoreCalloutMessage) {
      Object.assign(newState, { calloutMessageArr: [`Group ${groupName} was successfully removed.`] });
    }

    return $moduleConfig
      .saveModuleConfig({
        module: GCP_PROJECT_GROUPS_MODULE_GROUP,
        settings: { ...companyProjectGroups }
      })
      .finally(() => {
        this.setState(newState);
      });
  };

  getGcpProjectGroupOptions = memoize(
    () => {
      const { group } = initialFields;

      const gcpProjectGroups = Object.keys(this.companyGcpProjectGroups).map((gcpGroup) => ({
        value: gcpGroup,
        label: gcpGroup
      }));

      return [...group.options, ...gcpProjectGroups];
    },
    () => this.companyGcpProjectGroups
  );

  onManageGroupClicked = () => {
    const { form } = this.props;

    const selectedGroup = form.getValue('group');
    this.tempSelectedGroup = selectedGroup;

    this.projectsCollection.resetDiscreteFilters();

    this.setState({
      isManagingProjectGroup: true
    });
  };

  render() {
    const { $hybridMap, form } = this.props;
    const { popoverOpen, displayGroupDialog, isManagingProjectGroup, calloutMessageArr } = this.state;
    const { pageCount, currentPage } = this.projectsCollection?.pageState || {};

    return (
      <Popover
        autoFocus={false}
        enforceFocus={false}
        isOpen={popoverOpen}
        onClose={this.onPopoverClose}
        placement="bottom-start"
        content={
          <Suspense
            loading={$hybridMap.gcpCloudMapCollection.loading}
            fallback={
              <Flex p={2} alignItems="Center" justifyContent="center" width="100%" gap={1}>
                <Spinner size={24} />
                <Text>Loading GCP Projects.</Text>
              </Flex>
            }
          >
            <Card display="flex" flexDirection="column" width="750px">
              <Heading p={2} bg="subnavBackground" level={5} mb={0}>
                Project Filter
              </Heading>
              <Flex p={1} gap={1} alignItems="center">
                <Field name="name" width="100%" placeholder="Search Projects...">
                  <Filter collection={this.projectsCollection} />
                </Field>

                <Field
                  name="view"
                  onQuery={null}
                  onChange={this.onViewDropdownChange}
                  disabled={isManagingProjectGroup}
                >
                  <Select small menuWidth={100} />
                </Field>
                <Field
                  name="group"
                  disabled={isManagingProjectGroup}
                  options={this.getGcpProjectGroupOptions()}
                  onChange={this.onGroupDropdownChange}
                >
                  <Select small menuWidth={175} />
                </Field>
              </Flex>
              <Flex px={1} paddingTop="8px" alignItems="center" justifyContent="center" gap={1}>
                <Flex
                  flexGrow="1"
                  width="100%"
                  px={1}
                  justifyContent="space-between"
                  alignItems="center"
                  bg="subnavBackground"
                >
                  {isManagingProjectGroup && (
                    <Flex gap="1">
                      <Button small intent="primary" text="Save" onClick={this.saveGroup} />
                      <Button small intent="danger" text="Cancel" onClick={this.cancelManageGroup} />
                    </Flex>
                  )}
                  <Flex justifyContent="center" width="50px" style={{ paddingRight: '6px' }}>
                    <Checkbox
                      checked={!!this.projectsCollection.isAllSelected}
                      onChange={() => {
                        this.projectsCollection.selectAll({
                          multi: true
                        });
                      }}
                    />
                  </Flex>
                  <Flex flex="1" py="8px" px={1} justifyContent="space-between" alignItems="center">
                    <Text>{`${this.projectsCollection.selectedSize} / ${this.projectsCollection.unfilteredSize} selected`}</Text>
                  </Flex>
                  <Flex flex="1">
                    <Flex gap={2} ml="auto">
                      {form.getValue('group') === '' && (
                        <Button
                          small
                          intent="primary"
                          text="Create Group"
                          onClick={() => {
                            this.setState({ displayGroupDialog: true });
                          }}
                        />
                      )}

                      {form.getValue('group') !== '' && !isManagingProjectGroup && (
                        <>
                          <Button small text="Manage Group" onClick={this.onManageGroupClicked} />
                          <Button
                            small
                            text="Rename Group"
                            onClick={() => {
                              const selectedGroup = form.getValue('group');
                              this.tempSelectedGroup = selectedGroup;
                              return this.setState({ displayGroupDialog: true });
                            }}
                          />
                          <RemoveButton
                            buttonProps={{ small: true, intent: 'danger', text: 'Remove Group' }}
                            onRemove={this.removeGroup}
                          />
                        </>
                      )}
                    </Flex>
                  </Flex>
                </Flex>
              </Flex>
              <StyledBox p={1} className="flex-grow-1">
                <VirtualizedTable
                  hideHeader
                  isCollapsed
                  multiSelect
                  onRowClick={(model, event) => {
                    event?.stopPropagation?.();
                    if (isManagingProjectGroup) {
                      return;
                    }
                    model.select({ multi: true });
                  }}
                  style={{ height: 300 }}
                  collection={this.projectsCollection}
                  columns={this.columns}
                />
              </StyledBox>
              {/* Pagination */}
              {pageCount > 1 && (
                <Flex justifyContent="space-between" p={1} alignItems="center">
                  <Button onClick={() => this.changePage(currentPage - 1)} disabled={currentPage <= 1}>
                    Previous
                  </Button>
                  <Box flexGrow="1" textAlign="center">
                    <Text>
                      Page {currentPage} of {pageCount}
                    </Text>
                  </Box>
                  <Button onClick={() => this.changePage(currentPage + 1)} disabled={currentPage >= pageCount}>
                    Next
                  </Button>
                </Flex>
              )}

              {calloutMessageArr.length > 0 && (
                <TimedCallout timeout={5000} intent="success" key={calloutMessageArr.join('-')}>
                  {calloutMessageArr.map((message) => (
                    <Text key={message}>{message}</Text>
                  ))}
                </TimedCallout>
              )}

              <MapSearchGroupDialog
                isOpen={displayGroupDialog}
                selectedCount={this.projectsCollection.selectedSize}
                groupName={this.tempSelectedGroup}
                onSubmit={(groupName) => {
                  this.createGroup(groupName).then(() => {
                    form.setValue('group', groupName);
                    this.onGroupDropdownChange({ value: groupName });
                  });
                }}
                onClose={() => this.setState({ displayGroupDialog: false })}
              />
            </Card>
          </Suspense>
        }
      >
        <Flex alignItems="center">
          <Button
            rightIcon="caret-down"
            height="35px"
            active={popoverOpen}
            onClick={() => this.setState((prevState) => ({ popoverOpen: !prevState.popoverOpen }))}
            small
          >
            <Flex alignItems="center" gap={1} px={1} justifyContent="space-evenly" minWidth="135px">
              <Icon icon={GoogleLogo} />
              <div>Projects</div>
              <Tag bg="gcp.green" color="white.10" round small>
                {this.projectsCollection.selectedSize}
              </Tag>
            </Flex>
          </Button>
        </Flex>
      </Popover>
    );
  }
}
